import React, { useEffect, useRef, useState } from 'react';

const ChemicalImage = ({ src, alt }) => {
    const [isLoading, setIsLoading] = useState(true);
    return (
        <>
            {isLoading && <span style={{ fontSize: '0.8em', color: 'var(--text-dark)' }}>Loading Structure...</span>}
            <img
                src={src}
                alt={alt}
                style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    maxWidth: '38%',
                    minWidth: '90px',
                    display: isLoading ? 'none' : 'block'
                }}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
            />
        </>
    );
};

const KatexRenderer = ({ text }) => {
    const containerRef = useRef(null);
    useEffect(() => {
        if (!containerRef.current) return;
        // Always show plain text immediately as a safe fallback
        try { containerRef.current.textContent = text != null ? String(text) : ''; } catch {}
        if (window.katex) {
            const renderMathInText = (raw) => {
                const blockRegex = /\$\$(.*?)\$\$/g;
                const inlineRegex = /\$(.*?)\$/g;
                let html = String(raw).replace(blockRegex, (match, expression) => {
                    try {
                        return window.katex.renderToString(expression, { throwOnError: false, displayMode: true });
                    } catch (e) {
                        console.error('KaTeX rendering error:', e);
                        return match;
                    }
                });
                html = html.replace(inlineRegex, (match, expression) => {
                    if (match.startsWith('$$')) return match;
                    try {
                        return window.katex.renderToString(expression, { throwOnError: false, displayMode: false });
                    } catch (e) {
                        console.error('KaTeX rendering error:', e);
                        return match;
                    }
                });
                return html;
            };
            try { containerRef.current.innerHTML = renderMathInText(text); } catch {}
        }
    }, [text]);
    return <span ref={containerRef} />;
};

const ContentRenderer = ({ content, reactionSummary }) => {
    if (typeof content === 'object' && !Array.isArray(content) && content !== null && content.type === 'full_reaction') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <div className="reaction-container">
                    <div className="reaction-side">
                        {content.reactants.map((reactant, index) => (
                            <React.Fragment key={`reactant-${index}`}>
                                <ContentRenderer content={[reactant]} />
                                {index < content.reactants.length - 1 && <span className="plus-sign">+</span>}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="reaction-arrow-group">
                        <span className="reagents">{content.reagents.join(', ')}</span>
                        <span className="arrow">→</span>
                    </div>
                    <div className="reaction-side">
                        {content.products.map((product, index) => (
                            <React.Fragment key={`product-${index}`}>
                                <ContentRenderer content={[product]} />
                                {index < content.products.length - 1 && <span className="plus-sign">+</span>}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                {reactionSummary && (
                    <p style={{ margin: '0.75rem 0 0 0', fontStyle: 'italic', fontSize: '0.85em', maxWidth: '90%' }}>
                        {reactionSummary}
                    </p>
                )}
            </div>
        );
    }

    if (Array.isArray(content)) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {content.map((item, index) => {
                        const chemRegex = /CHEM\[(.*?)\]/;
                        if (typeof item === 'string') {
                            const match = item.match(chemRegex);
                            if (match) {
                                const chemicalName = encodeURIComponent(match[1]);
                                const imageUrl = `https://cactus.nci.nih.gov/chemical/structure/${chemicalName}/image?format=png&width=300&height=300`;
                                return (
                                    <React.Fragment key={index}>
                                        <ChemicalImage src={imageUrl} alt={`Structure of ${match[1]}`} />
                                        {index < content.length - 1 && <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>→</span>}
                                    </React.Fragment>
                                );
                            }
                            // Fallback: render as text/KaTeX when not a CHEM macro
                            return <span key={index}><KatexRenderer text={item} /></span>;
                        }
                        // For nested arrays/objects, delegate back to ContentRenderer
                        return <span key={index}><ContentRenderer content={item} /></span>;
                    })}
                </div>
                {reactionSummary && (
                    <p style={{ margin: '0.75rem 0 0 0', fontStyle: 'italic', fontSize: '0.85em', maxWidth: '90%' }}>
                        {reactionSummary}
                    </p>
                )}
            </div>
        );
    }

    if (typeof content === 'string') {
        const reactionRegex = /SMILES\[(.*?)\]>>SMILES\[(.*?)\]/;
        const reactionMatch = content.match(reactionRegex);
        if (reactionMatch) {
            const reactantSmiles = encodeURIComponent(reactionMatch[1]);
            const productSmiles = encodeURIComponent(reactionMatch[2]);
            const reactantImageUrl = `https://cactus.nci.nih.gov/chemical/structure/${reactantSmiles}/image?format=png&width=400&height=400`;
            const productImageUrl = `https://cactus.nci.nih.gov/chemical/structure/${productSmiles}/image?format=png&width=400&height=400`;
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <ChemicalImage src={reactantImageUrl} alt="Reactant Structure" />
                    <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>→</span>
                    <ChemicalImage src={productImageUrl} alt="Product Structure" />
                </div>
            );
        }
        const singleMoleculeRegex = /SMILES\[(.*?)\]/;
        const singleMatch = content.match(singleMoleculeRegex);
        if (singleMatch) {
            const smiles = encodeURIComponent(singleMatch[1]);
            const imageUrl = `https://cactus.nci.nih.gov/chemical/structure/${smiles}/image?format=png&width=500&height=500`;
            return <ChemicalImage src={imageUrl} alt="Chemical Structure" />;
        }
        return <KatexRenderer text={content} />;
    }

    return null;
};

export default ContentRenderer;


